import React, { useEffect, useMemo, useState } from "react";
import { Paper, Typography, Select, MenuItem, Button, Grid, TextField, Snackbar, Alert } from "@mui/material";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";
import { useAuth } from "../../../context/AuthContext";
import { listSubscriptions, listPlans, setSubscription, listTenants, createSubscriptionInvoice, listSubscriptionBills, paySubscriptionInvoice, sendSubscriptionBill, getSubscriptionInvoicePreview } from "../../../services/admin";
import { Link } from "react-router-dom";

type Status = 'active' | 'past_due' | 'canceled' | 'trialing' | 'suspended';

export default function SubscriptionsPage() {
  useProtectedRoute();
  const { token, role } = useAuth();
  const can = role === 'SUPERADMIN';

  const [subs, setSubs] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastSeverity, setToastSeverity] = useState<'success'|'error'|'info'>('success');
  const [filter, setFilter] = useState("");
  const [periodInputs, setPeriodInputs] = useState<Record<string, { count: number; unit: 'month' | 'year' | 'day' }>>({});
  const [latestBill, setLatestBill] = useState<Record<string, any>>({});

  const planCodes = useMemo(() => plans.map((p:any) => p.code), [plans]);
  const statuses: Status[] = ['active','past_due','canceled','trialing','suspended'];

  useEffect(() => {
    (async () => {
      if (!token || !can) return;
      setLoading(true); setError(null);
      try {
        const [s, p, t] = await Promise.all([listSubscriptions(token), listPlans(token), listTenants(token)]);
        setSubs(s); setPlans(p); setTenants(t);
        // load latest bills per tenant
        const bills = await listSubscriptionBills(token);
        const map: Record<string, any> = {};
        bills.forEach((b:any) => { const id = String(b.tenantId); if (!map[id] || new Date(map[id].createdAt) < new Date(b.createdAt)) map[id] = b; });
        setLatestBill(map);
      } catch (e: any) {
        setError(e?.response?.data?.message || 'Failed to load subscriptions');
      } finally { setLoading(false); }
    })();
  }, [token, can]);

  const apply = async (tenantId: string | any, payload: Partial<{ planCode: string; status: Status }>) => {
    if (!token) return;
    setLoading(true); setError(null);
    try {
      const id = String(tenantId);
      const updated = await setSubscription(token, id, payload);
      setSubs(prev => prev.map(x => String(x.tenantId) === id ? updated : x));
    } catch (e:any) {
      setError(e?.response?.data?.message || 'Failed to update subscription');
    } finally { setLoading(false); }
  };

  const applyPeriod = async (tenantId: string) => {
    const key = String(tenantId);
    const v = periodInputs[key] || { count: 1, unit: 'month' };
    await apply(key, { periodCount: v.count as any, periodUnit: v.unit as any } as any);
  };

  const generateInvoice = async (tenantId: string) => {
    if (!token) return;
    setLoading(true); setError(null);
    try {
      const pi = periodInputs[String(tenantId)] || { count: 1, unit: 'month' };
      const bill = await createSubscriptionInvoice(token, tenantId, { periodUnit: pi.unit, periodCount: pi.count });
      setLatestBill(prev => ({ ...prev, [String(tenantId)]: bill }));
    } catch (e:any) { setError(e?.response?.data?.message || 'Failed to create invoice'); } finally { setLoading(false); }
  };

  const markPaid = async (tenantId: string) => {
    if (!token) return;
    const bill = latestBill[String(tenantId)];
    if (!bill) { setError('No invoice to pay'); return; }
    setLoading(true); setError(null);
    try {
      const paid = await paySubscriptionInvoice(token, bill._id, { amount: bill.amount, method: 'manual' });
      setLatestBill(prev => ({ ...prev, [String(tenantId)]: paid }));
      await sendSubscriptionBill(token, bill._id);
    } catch (e:any) { setError(e?.response?.data?.message || 'Failed to record payment'); } finally { setLoading(false); }
  };

  const previewInvoice = async (tenantId: string) => {
    if (!token) return;
    const bill = latestBill[String(tenantId)];
    if (!bill) { setError('No bill to preview'); return; }
    try {
      const html = await getSubscriptionInvoicePreview(token, bill._id);
      const w = window.open("", "_blank");
      if (w) { w.document.open(); w.document.write(html); w.document.close(); }
    } catch (e:any) { setError(e?.response?.data?.message || 'Failed to load preview'); }
  };

  const sendBillVia = async (tenantId: string, channel: 'email'|'whatsapp') => {
    if (!token) return;
    const bill = latestBill[String(tenantId)];
    if (!bill) { setError('No bill to send'); setToastSeverity('error'); setToastMsg('No bill to send'); setToastOpen(true); return; }
    setLoading(true); setError(null);
    try {
      await sendSubscriptionBill(token, bill._id, { channel });
      setToastSeverity('success');
      setToastMsg(`${channel === 'email' ? 'Email' : 'WhatsApp'} sent`);
      setToastOpen(true);
    } catch (e:any) {
      const msg = e?.response?.data?.message || `Failed to send via ${channel}`;
      setError(msg); setToastSeverity('error'); setToastMsg(msg); setToastOpen(true);
    } finally { setLoading(false); }
  };

  const tenantNameById = useMemo(() => Object.fromEntries(tenants.map((t:any) => [String(t._id), t.name])), [tenants]);

  const filtered = useMemo(() => {
    if (!filter) return subs;
    return subs.filter((s:any) => (s.tenantId || '').toString().toLowerCase().includes(filter.toLowerCase()) || (s.planCode || '').toLowerCase().includes(filter.toLowerCase()) || (s.status || '').toLowerCase().includes(filter.toLowerCase()));
  }, [subs, filter]);

  if (!can) return <Paper elevation={0} className="p-6 rounded-2xl"><Typography>You do not have access.</Typography></Paper>;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="flex items-center justify-between mb-3">
        <Typography variant="h5" fontWeight={900}>SaaS Admin - Subscriptions</Typography>
        <div className="flex gap-2">
          <Button component={Link} to="/control" size="small" variant="outlined">Tenants</Button>
          <Button component={Link} to="/control/plans" size="small" variant="outlined">Plans</Button>
          <Button component={Link} to="/control/billing" size="small" variant="outlined">Billing</Button>
        </div>
      </div>
      {error && <Typography color="error" className="mb-2">{error}</Typography>}

      <Paper elevation={0} className="p-3 rounded-2xl mb-3" style={{ border: '1px solid var(--mui-palette-divider)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}><TextField fullWidth size="small" label="Filter by tenantId / plan / status" value={filter} onChange={(e)=>setFilter(e.target.value)} /></Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} className="p-0 overflow-x-auto rounded-2xl" style={{ border: '1px solid var(--mui-palette-divider)' }}>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-200">
            <tr>
              <th className="py-2 px-3 text-left text-gray-700">Tenant</th>
              <th className="py-2 px-3 text-left text-gray-700">Plan</th>
              <th className="py-2 px-3 text-left text-gray-700">Status</th>
              <th className="py-2 px-3 text-left text-gray-700">Period</th>
              <th className="py-2 px-3 text-left text-gray-700">Latest Bill</th>
              <th className="py-2 px-3 text-left text-gray-700">Set Duration</th>
              <th className="py-2 px-3 text-center text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!filtered.length && !loading && (
              <tr><td colSpan={5} className="text-center text-gray-400 py-6">No subscriptions</td></tr>
            )}
            {filtered.map((s:any) => (
              <tr key={s._id} className="border-b border-gray-200">
                <td className="py-2 px-3">{s.tenant?.name || tenantNameById[String(s.tenantId)] || String(s.tenantId)}</td>
                <td className="py-2 px-3">
                  <Select size="small" value={s.planCode} onChange={(e)=>apply(String(s.tenantId), { planCode: e.target.value as string })}>
                    {planCodes.map((c:string) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </td>
                <td className="py-2 px-3">
                  <Select size="small" value={s.status} onChange={(e)=>apply(String(s.tenantId), { status: e.target.value as Status })}>
                    {statuses.map(st => <MenuItem key={st} value={st}>{st}</MenuItem>)}
                  </Select>
                </td>
                <td className="py-2 px-3">{s.currentPeriodStart ? new Date(s.currentPeriodStart).toLocaleDateString() : '-'} to {s.currentPeriodEnd ? new Date(s.currentPeriodEnd).toLocaleDateString() : '-'}</td>
                <td className="py-2 px-3">
                  {latestBill[String(s.tenantId)] ? (
                    <div>
                      <div>{latestBill[String(s.tenantId)].invoiceNumber} - {Number(latestBill[String(s.tenantId)].amount).toFixed(2)} {latestBill[String(s.tenantId)].currency}</div>
                      <div>Status: {latestBill[String(s.tenantId)].status}</div>
                    </div>
                  ) : (
                    <span className="text-gray-400">No bill</span>
                  )}
                </td>
                <td className="py-2 px-3">
                  <div className="flex items-center gap-2">
                    <TextField size="small" type="number" value={(periodInputs[String(s.tenantId)]?.count ?? 1)} onChange={(e)=>setPeriodInputs(prev=>({ ...prev, [String(s.tenantId)]: { ...(prev[String(s.tenantId)]||{ unit:'month', count:1 }), count: Math.max(1, Number(e.target.value||1)) } }))} style={{ width: 90 }} />
                    <Select size="small" value={(periodInputs[String(s.tenantId)]?.unit ?? 'month')} onChange={(e)=>setPeriodInputs(prev=>({ ...prev, [String(s.tenantId)]: { ...(prev[String(s.tenantId)]||{ count:1 }), unit: e.target.value as any } }))}>
                      <MenuItem value="day">Days</MenuItem>
                      <MenuItem value="month">Months</MenuItem>
                      <MenuItem value="year">Years</MenuItem>
                    </Select>
                    <Button size="small" variant="outlined" onClick={()=>applyPeriod(String(s.tenantId))}>Apply</Button>
                    <Button size="small" variant="outlined" onClick={()=>generateInvoice(String(s.tenantId))}>Create Invoice</Button>
                    <Button size="small" variant="outlined" onClick={()=>previewInvoice(String(s.tenantId))}>Preview</Button>
                  </div>
                </td>
                <td className="py-2 px-3 text-center">
                  <Button size="small" variant="outlined" onClick={()=>apply(String(s.tenantId), { status: s.status === 'active' ? 'suspended' : 'active' })}>
                    {s.status === 'active' ? 'Suspend' : 'Activate'}
                  </Button>
                  <Button size="small" variant="outlined" className="ml-2" onClick={()=>markPaid(String(s.tenantId))}>Mark Paid</Button>
                  <Button size="small" variant="outlined" className="ml-2" onClick={()=>sendBillVia(String(s.tenantId), 'email')}>Email</Button>
                  <Button size="small" variant="outlined" className="ml-2" onClick={()=>sendBillVia(String(s.tenantId), 'whatsapp')}>WhatsApp</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div className="text-center py-6">Loading...</div>}
      </Paper>
      <Snackbar open={toastOpen} autoHideDuration={3000} onClose={()=>setToastOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={()=>setToastOpen(false)} severity={toastSeverity} variant="filled" sx={{ width: '100%' }}>
          {toastMsg}
        </Alert>
      </Snackbar>
    </div>
  );
}

